import { useLocation, useNavigate } from 'react-router-dom';
import { useRef, useEffect, useState, useCallback } from 'react';
import { LayoutDashboard, BellRing, Search, Settings, Wallet } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useUserContext } from '@/hooks/useUserContext';
import { useTranslation } from '@/contexts/i18nContext';
import { useTenant } from '@/contexts/TenantContext';
// Check if user appears to be logged in (session exists in storage)
const hasActiveSession = (): boolean => {
  try {
    // Check for Supabase session in localStorage
    const storageKey = Object.keys(localStorage).find(key => 
      key.startsWith('sb-') && key.endsWith('-auth-token')
    );
    if (storageKey) {
      const data = localStorage.getItem(storageKey);
      if (data) {
        const parsed = JSON.parse(data);
        return !!parsed?.access_token;
      }
    }
    return false;
  } catch {
    return false;
  }
};

interface NavItem {
  icon: React.ElementType;
  labelKey: string;
  path: string;
}

const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { primaryRole, isLoading: contextLoading, isGodMode, isAdmin, isSovereign, isDemiUser } = useUserContext();
  const { t } = useTranslation();
  const { hasFeature, isLoading: tenantLoading } = useTenant();

  // Show nav if we have a user OR if there's a cached session (instant display)
  const shouldShowNav = user || hasActiveSession();

  // Determine the correct home path based on role
  const getHomePath = () => {
    if (isGodMode) return '/god-mode';
    if (isAdmin) return '/admin';
    if (isSovereign) return '/sovereign';
    if (isDemiUser) return '/guest';
    return '/dashboard';
  };

  // Check feature flags (God mode always sees everything)
  const showConcierge = isGodMode || hasFeature('concierge');
  const showRentals = isGodMode || hasFeature('rentals');
  const showPayments = isGodMode || hasFeature('hoa_payments');

  // Build nav items based on role - uses cached role for instant display
  const buildNavItems = (): NavItem[] => {
    const items: NavItem[] = [];

    if (isDemiUser) {
      if (showConcierge) items.push({ icon: BellRing, labelKey: 'nav.concierge', path: '/concierge' });
      if (showRentals) items.push({ icon: Search, labelKey: 'nav.browse', path: '/browse-rentals' });
      if (showPayments) items.push({ icon: Wallet, labelKey: 'nav.wallet', path: '/wallet' });
      items.push({ icon: Settings, labelKey: 'nav.settings', path: '/settings' });
      return items;
    }

    items.push({ icon: LayoutDashboard, labelKey: 'nav.home', path: getHomePath() });
    if (showConcierge) items.push({ icon: BellRing, labelKey: 'nav.concierge', path: '/concierge' });
    if (showRentals) items.push({ icon: Search, labelKey: 'nav.browse', path: '/browse-rentals' });
    if (showPayments) items.push({ icon: Wallet, labelKey: 'nav.wallet', path: '/wallet' });
    items.push({ icon: Settings, labelKey: 'nav.settings', path: '/settings' });
    return items;
  };

  const navItems = buildNavItems();

  const isActive = (path: string) => {
    if (path === getHomePath() && !isDemiUser) {
      return location.pathname === '/dashboard' || 
             location.pathname === '/god-mode' || 
             location.pathname === '/admin' || 
             location.pathname === '/sovereign';
    }
    if (path === '/concierge') {
      return location.pathname === '/concierge' || 
             location.pathname === '/services' || 
             location.pathname.startsWith('/services');
    }
    if (path === '/wallet') {
      return location.pathname === '/wallet' || location.pathname.startsWith('/wallet/');
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const triggerHaptic = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
  };

  const activeIndex = navItems.findIndex(item => isActive(item.path));
  
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 24 });
  
  const updateIndicatorPosition = useCallback(() => {
    if (activeIndex < 0 || !buttonRefs.current[activeIndex] || !containerRef.current) return;
    
    const button = buttonRefs.current[activeIndex];
    const container = containerRef.current;
    
    if (button && container) {
      const buttonRect = button.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const buttonCenter = buttonRect.left + buttonRect.width / 2 - containerRect.left;
      
      setIndicatorStyle({
        left: buttonCenter - 12,
        width: 24,
      });
    }
  }, [activeIndex]);
  
  useEffect(() => {
    updateIndicatorPosition();
    window.addEventListener('resize', updateIndicatorPosition);
    return () => window.removeEventListener('resize', updateIndicatorPosition);
  }, [updateIndicatorPosition, navItems.length]);

  // Don't show nav if definitely not logged in (after all hooks)
  if (!shouldShowNav) return null;

  return (
    <nav className="fixed left-0 right-0 z-50 bottom-[max(0.75rem,env(safe-area-inset-bottom))] flex justify-center px-3 animate-nav-entrance pointer-events-none">
      <div className="relative pointer-events-auto">
        {/* Glow effect layer */}
        <div 
          className="absolute inset-0 rounded-[24px] sm:rounded-[28px] opacity-50 blur-xl -z-10 transition-all duration-500"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(212, 175, 55, 0.15) 0%, transparent 70%)',
          }}
        />
        {/* Combined background + content layer */}
        <div 
          ref={containerRef}
          className="relative flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 sm:py-3 rounded-[24px] sm:rounded-[28px] border border-white/15"
          style={{
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.85) 0%, rgba(15, 23, 42, 0.90) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.1), 0 25px 50px -12px rgba(0,0,0,0.6)',
          }}
        >
          {/* Sliding indicator */}
          {activeIndex >= 0 && (
            <div
              className="absolute bottom-1.5 sm:bottom-2 h-0.5 bg-primary rounded-full transition-all duration-300 ease-out"
              style={{
                width: `${indicatorStyle.width}px`,
                left: `${indicatorStyle.left}px`,
                boxShadow: '0 0 8px rgba(212, 175, 55, 0.6)',
              }}
            />
          )}
          {navItems.map((item, index) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                ref={(el) => { buttonRefs.current[index] = el; }}
                onClick={() => {
                  triggerHaptic();
                  navigate(item.path);
                }}
                className={`relative flex flex-col items-center gap-0.5 sm:gap-1 px-2 sm:px-3 py-1.5 sm:py-2 rounded-xl sm:rounded-2xl min-w-0 transition-all duration-300 ease-out ${
                  active
                    ? 'text-primary scale-105'
                    : 'text-muted-foreground hover:text-foreground hover:bg-white/5 hover:scale-102'
                }`}
                style={{
                  transform: active ? 'scale(1.05)' : 'scale(1)',
                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                }}
              >
                {/* Active glow effect with fade animation */}
                <div 
                  className="absolute inset-0 rounded-xl sm:rounded-2xl transition-opacity duration-300"
                  style={{
                    background: 'radial-gradient(ellipse at center, rgba(212, 175, 55, 0.25) 0%, transparent 70%)',
                    opacity: active ? 1 : 0,
                  }}
                />
                <div 
                  className="relative w-8 h-8 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ease-out"
                  style={{
                    background: active ? 'rgba(212, 175, 55, 0.2)' : 'transparent',
                    boxShadow: active ? '0 4px 12px rgba(212, 175, 55, 0.2)' : 'none',
                  }}
                >
                  <Icon 
                    className="w-5 h-5 sm:w-6 sm:h-6 transition-all duration-300 ease-out"
                    style={{
                      color: active ? 'hsl(var(--primary))' : undefined,
                      transform: active ? 'scale(1.1)' : 'scale(1)',
                    }}
                  />
                </div>
                <span 
                  className="relative text-[9px] sm:text-[10px] font-medium truncate max-w-[48px] sm:max-w-none transition-all duration-300"
                  style={{
                    color: active ? 'hsl(var(--primary))' : undefined,
                    opacity: active ? 1 : 0.8,
                  }}
                >
                  {t(item.labelKey)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default BottomNav;
